import { useState } from "react";
import { useT } from "../i18n";
import type { SessionTab } from "../vault/sessions";
import { launchRdpAdhoc } from "../desktop/bridge";

// Ad-hoc quick-connect bar (desktop only). Type a host + credentials to open an SSH/RDP session
// without a saved item. The password is held only in this form's state and passed straight to the
// session (which hands it to the native command); it is cleared right after launch and never
// persisted. Host key / server cert is trust-on-first-use.
export function QuickConnect({ onOpen }: { onOpen: (tab: Omit<SessionTab, "key">) => string }) {
  const t = useT();
  const [proto, setProto] = useState<"ssh" | "rdp">("ssh");
  const [host, setHost] = useState("");
  const [port, setPort] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [domain, setDomain] = useState("");

  const defaultPort = proto === "ssh" ? 22 : 3389;
  const canConnect = host.trim() !== "" && username.trim() !== "";

  function connect() {
    if (!canConnect) return;
    const p = parseInt(port, 10);
    const portNum = Number.isFinite(p) && p > 0 ? p : defaultPort;
    const title = `${host.trim()}${port ? ":" + portNum : ""}`;
    if (proto === "ssh") {
      onOpen({
        kind: "ssh",
        title,
        launch: { mode: "adhoc-ssh", params: { host: host.trim(), port: portNum, username: username.trim(), password } },
      });
    } else {
      // RDP launches the platform's native client in its own window (not an app tab).
      void launchRdpAdhoc({ host: host.trim(), port: portNum, domain: domain.trim(), username: username.trim(), password }).catch((e) =>
        window.alert(`${t("RDP launch failed")}: ${e instanceof Error ? e.message : String(e)}`),
      );
    }
    setPassword(""); // never retain the typed secret
  }

  const input = "rounded border border-border bg-panel2 px-2 py-1 text-xs outline-none focus:border-accent";
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        connect();
      }}
      className="flex flex-wrap items-center gap-2 border-b border-border bg-panel px-3 py-1.5"
    >
      <span className="text-[11px] font-semibold tracking-wide text-muted">{t("Quick connect")}</span>
      <select value={proto} onChange={(e) => setProto(e.target.value as "ssh" | "rdp")} className={input}>
        <option value="ssh">SSH</option>
        <option value="rdp">RDP</option>
      </select>
      <input className={`${input} w-40`} placeholder={t("Host")} value={host} onChange={(e) => setHost(e.target.value)} />
      <input className={`${input} w-16`} placeholder={String(defaultPort)} value={port} onChange={(e) => setPort(e.target.value)} />
      {proto === "rdp" && (
        <input className={`${input} w-24`} placeholder={t("Domain")} value={domain} onChange={(e) => setDomain(e.target.value)} />
      )}
      <input className={`${input} w-32`} placeholder={t("Username")} value={username} onChange={(e) => setUsername(e.target.value)} />
      <input
        className={`${input} w-32`}
        type="password"
        placeholder={t("Password")}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="off"
      />
      <button
        type="submit"
        disabled={!canConnect}
        className="rounded bg-accent px-3 py-1 text-xs font-medium text-white hover:bg-accent2 disabled:opacity-50"
      >
        ▶ {t("Connect")}
      </button>
    </form>
  );
}
